/**
 * Document Preview Store
 *
 * Global state for the document preview modal. Both Library and Chat
 * call openPreview() to display a document — no coupling between stores.
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import { getViewUrl, getDownloadUrl, getHtmlUrl, type ConversionStatus } from '@/lib/api'

// ===================
// Types
// ===================

/**
 * Bounding box coordinates in PDF user space
 */
export interface Rect {
  x1: number
  y1: number
  x2: number
  y2: number
}

/**
 * Highlight data for PDF citation rendering (V1 bounding boxes)
 */
export interface HighlightData {
  id: string
  pages: Array<{
    pageNumber: number
    rects: Rect[]
  }>
}

/**
 * Text highlight data for citation rendering via text search (V2)
 * Works for PDF (text layer), DOCX, and HTML viewers.
 */
export interface TextHighlight {
  chunkText: string
  sectionHeader: string | null
}

/** MIME types eligible for server-generated HTML preview */
const HTML_PREVIEW_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/plain',
  'text/csv',
])

export interface PreviewState {
  isOpen: boolean
  documentId: string | null
  documentName: string | null
  mimeType: string | null
  size: number | null
  page: number | null
  /**
   * 'pack' is its own context (design decision D-12): the Submission Pack page
   * closes the preview on unmount the way Analysis does, but nothing docks a
   * reader there — reusing 'analysis' would make pack previews vanish on wide
   * screens, because `useDockedAnalysisPreview` would claim them.
   */
  context: 'library' | 'chat' | 'analysis' | 'pack' | null
  citationLabel: string | null

  // PDF highlight support (V1)
  highlights: HighlightData[]
  activeHighlightId: string | null

  // Text highlight support (V2 — non-PDF)
  textHighlight: TextHighlight | null

  // HTML preview URL (for Excel/TXT/CSV)
  htmlUrl: string | null

  // Canonical PDF pipeline (Migration 114). When the doc was uploaded as a
  // non-PDF (DOCX, XLSX, ...), the viewer is showing a converted PDF, and
  // these fields let the header surface a separate "Download original" button.
  originalDownloadUrl: string | null
  originalFileName: string | null
  conversionStatus: ConversionStatus | null

  // Presigned URL cache
  url: string | null
  urlExpiresAt: number | null
  isLoading: boolean
  error: string | null

  // Actions
  openPreview: (params: OpenPreviewParams) => Promise<void>
  closePreview: () => void
  setPage: (page: number) => void
  downloadDocument: () => Promise<void>
  addHighlight: (highlight: HighlightData) => void
  removeHighlight: (id: string) => void
  clearHighlights: () => void
  setActiveHighlight: (id: string | null) => void
}

export interface OpenPreviewParams {
  documentId: string
  documentName?: string
  page?: number
  context?: 'library' | 'chat' | 'analysis' | 'pack'
  citationLabel?: string
  highlight?: HighlightData
  textHighlight?: TextHighlight
}

// ===================
// Constants
// ===================

/** Buffer before expiry to trigger refresh (30 seconds) */
const URL_REFRESH_BUFFER_MS = 30_000

// ===================
// Store
// ===================

export const usePreviewStore = create<PreviewState>()(
  devtools(
    (set, get) => ({
      isOpen: false,
      documentId: null,
      documentName: null,
      mimeType: null,
      size: null,
      page: null,
      context: null,
      citationLabel: null,
      highlights: [],
      activeHighlightId: null,
      textHighlight: null,
      htmlUrl: null,
      originalDownloadUrl: null,
      originalFileName: null,
      conversionStatus: null,
      url: null,
      urlExpiresAt: null,
      isLoading: false,
      error: null,

      openPreview: async (params) => {
        const { documentId, documentName, page, context, citationLabel, highlight, textHighlight } = params
        const current = get()

        // If same document and URL still valid, just update page/context/highlights
        if (
          current.documentId === documentId &&
          current.url &&
          current.urlExpiresAt &&
          Date.now() < current.urlExpiresAt - URL_REFRESH_BUFFER_MS
        ) {
          // Determine target page (from highlight or explicit param)
          const targetPage = highlight?.pages[0]?.pageNumber ?? page ?? current.page

          // Update highlights if new one provided
          const newHighlights = highlight
            ? [...current.highlights.filter(h => h.id !== highlight.id), highlight]
            : current.highlights

          set({
            isOpen: true,
            page: targetPage,
            context: context ?? current.context,
            citationLabel: citationLabel ?? null,
            highlights: newHighlights,
            activeHighlightId: highlight?.id ?? null,
            // A rects-only citation must not inherit a previous citation's quote —
            // the viewer suppresses rect overlays whenever a quote is present.
            textHighlight: textHighlight ?? (highlight ? null : current.textHighlight),
          })

          // Fetch HTML URL if textHighlight provided but no htmlUrl cached yet
          if (textHighlight && !current.htmlUrl && current.mimeType && HTML_PREVIEW_MIME_TYPES.has(current.mimeType)) {
            getHtmlUrl(documentId)
              .then((res) => {
                if (res.htmlUrl) set({ htmlUrl: res.htmlUrl })
              })
              .catch(() => { /* Non-fatal */ })
          }

          return
        }

        // Determine initial page from highlight
        const initialPage = highlight?.pages[0]?.pageNumber ?? page ?? 1

        // Fetch new view URL
        set({
          isOpen: true,
          documentId,
          documentName: documentName ?? null,
          mimeType: null,
          size: null,
          page: initialPage,
          context: context ?? null,
          citationLabel: citationLabel ?? null,
          highlights: highlight ? [highlight] : [],
          activeHighlightId: highlight?.id ?? null,
          textHighlight: textHighlight ?? null,
          htmlUrl: null,
          originalDownloadUrl: null,
          originalFileName: null,
          conversionStatus: null,
          url: null,
          urlExpiresAt: null,
          isLoading: true,
          error: null,
        })

        try {
          // Fetch view URL and HTML URL in parallel (HTML URL is non-fatal)
          const [viewResult, htmlResult] = await Promise.allSettled([
            getViewUrl(documentId),
            getHtmlUrl(documentId),
          ])

          if (viewResult.status === 'rejected') {
            throw viewResult.reason
          }

          const response = viewResult.value
          const htmlUrl = htmlResult.status === 'fulfilled' ? htmlResult.value.htmlUrl : null

          set({
            url: response.viewUrl,
            mimeType: response.mimeType,
            documentName: response.name,
            size: response.size,
            urlExpiresAt: Date.now() + response.expiresIn * 1000,
            htmlUrl,
            originalDownloadUrl: response.originalDownloadUrl,
            originalFileName: response.originalFileName,
            conversionStatus: response.conversionStatus,
            isLoading: false,
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load document',
          })
        }
      },

      closePreview: () => {
        set({
          isOpen: false,
          page: null,
          context: null,
          citationLabel: null,
          highlights: [],
          activeHighlightId: null,
          textHighlight: null,
          htmlUrl: null,
          originalDownloadUrl: null,
          originalFileName: null,
          conversionStatus: null,
          error: null,
        })
        // Keep documentId/url cached so re-opening is instant
      },

      setPage: (page) => {
        set({ page })
      },

      downloadDocument: async () => {
        const { documentId } = get()
        if (!documentId) return

        try {
          const url = await getDownloadUrl(documentId)
          window.open(url, '_blank')
        } catch (error) {
          console.error('Download failed:', error)
        }
      },

      addHighlight: (highlight) => {
        const current = get()
        // Avoid duplicates
        if (current.highlights.some(h => h.id === highlight.id)) return
        set({
          highlights: [...current.highlights, highlight],
          activeHighlightId: highlight.id,
        })
      },

      removeHighlight: (id) => {
        const current = get()
        set({
          highlights: current.highlights.filter(h => h.id !== id),
          activeHighlightId: current.activeHighlightId === id ? null : current.activeHighlightId,
        })
      },

      clearHighlights: () => {
        set({ highlights: [], activeHighlightId: null })
      },

      setActiveHighlight: (id) => {
        set({ activeHighlightId: id })
      },
    }),
    { name: 'preview-store' }
  )
)
